import React, { useRef, useState, useEffect } from 'react';
import { Box, Text, Button, Modal } from '@mantine/core';

// Parse inline formatting: **bold**, 𝐁𝐨𝐥𝐝 unicode, and plain text
const parseInline = (text) => {
    // Convert markdown **bold**
    const mdBoldRegex = /\*\*(.*?)\*\*/g;
    const parts = [];
    let last = 0;
    let match;

    // eslint-disable-next-line no-cond-assign
    while ((match = mdBoldRegex.exec(text)) !== null) {
        if (match.index > last) parts.push({ type: 'text', value: text.slice(last, match.index) });
        parts.push({ type: 'bold', value: match[1] });
        last = match.index + match[0].length;
    }
    if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });

    return parts.length > 0 ? parts : [{ type: 'text', value: text }];
};

const isUnicodeBold = (str) => /[\u{1D400}-\u{1D7FF}]/u.test(str);

const InlineText = ({ text, baseStyle }) => {
    if (isUnicodeBold(text)) {
        return <Text component="span" style={{ ...baseStyle, fontWeight: 700 }}>{text}</Text>;
    }
    const parts = parseInline(text);
    return (
        <>
            {parts.map((part, i) =>
                part.type === 'bold' ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={i} component="span" style={{ ...baseStyle, fontWeight: 700 }}>{part.value}</Text>
                ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={i} component="span" style={baseStyle}>{part.value}</Text>
                )
            )}
        </>
    );
};

// Detect line type
const getLineType = (line) => {
    if (!line.trim()) return 'empty';
    if (/^\d+\.\s/.test(line)) return 'numbered';       // 1. 2. 3.
    if (/^[a-z]\)\s/i.test(line)) return 'alpha';       // a) b) c)
    if (/^[ivxlcdm]+\)\s/i.test(line)) return 'roman';  // i) ii) iii)
    if (/^-\s/.test(line)) return 'bullet';              // - item
    if (/^\*\s/.test(line)) return 'bullet';             // * item
    if (/^\*\*(.*?)\*\*$/.test(line.trim())) return 'boldHeading'; // **Heading**
    if (isUnicodeBold(line)) return 'boldHeading';
    return 'paragraph';
};

const getIndentLevel = (type) => {
    if (type === 'numbered') return 0;
    if (type === 'alpha') return 1;
    if (type === 'roman') return 2;
    if (type === 'bullet') return 3;
    return 0;
};

const INDENT = 16; // px per level

const PolicyLine = ({ line }) => {
    const type = getLineType(line);
    const baseTextStyle = { fontSize: 14, color: '#344054', lineHeight: 1.75 };

    if (type === 'empty') return <Box style={{ height: 6 }} />;

    if (type === 'boldHeading') {
        const clean = line.replace(/\*\*/g, '');
        return (
            <Box style={{ marginBottom: 6, marginTop: 10 }}>
                <Text style={{ fontSize: 14, fontWeight: 700, color: '#1D2939', lineHeight: 1.6 }}>
                    {clean}
                </Text>
            </Box>
        );
    }

    if (type === 'numbered') {
        const numMatch = line.match(/^(\d+\.)\s(.*)/s);
        return (
            <Box style={{ display: 'flex', gap: 10, marginBottom: 8}}>
                <Text style={{ ...baseTextStyle, fontWeight: 700, color: '#FA5000', minWidth: 20 }}>
                    {numMatch?.[1]}
                </Text>
                <Text style={{ ...baseTextStyle, fontWeight: 600, color: '#1D2939' }}>
                    <InlineText text={numMatch?.[2] ?? ''} baseStyle={{ ...baseTextStyle, fontWeight: 600, color: '#1D2939' }} />
                </Text>
            </Box>
        );
    }

    if (type === 'alpha') {
        const alphaMatch = line.match(/^([a-z]\))\s(.*)/si);
        return (
            <Box style={{ display: 'flex', gap: 10, marginBottom: 6, paddingLeft: getIndentLevel(type) * INDENT }}>
                <Text style={{ ...baseTextStyle, fontWeight: 700, color: '#FA5000', minWidth: 18 }}>
                    {alphaMatch?.[1]}
                </Text>
                <Text style={baseTextStyle}>
                    <InlineText text={alphaMatch?.[2] ?? ''} baseStyle={baseTextStyle} />
                </Text>
            </Box>
        );
    }

    if (type === 'roman') {
        const romanMatch = line.match(/^([ivxlcdmIVXLCDM]+\))\s(.*)/si);
        return (
            <Box style={{ display: 'flex', gap: 10, marginBottom: 6, paddingLeft: getIndentLevel(type) * INDENT }}>
                <Text style={{ ...baseTextStyle, fontWeight: 600, color: '#667085', minWidth: 24, fontStyle: 'italic' }}>
                    {romanMatch?.[1]}
                </Text>
                <Text style={{ ...baseTextStyle, fontWeight: 600, color: '#1D2939' }}>
                    <InlineText text={romanMatch?.[2] ?? ''} baseStyle={{ ...baseTextStyle, fontWeight: 600 }} />
                </Text>
            </Box>
        );
    }

    if (type === 'bullet') {
        const bulletContent = line.replace(/^[-*]\s/, '');
        return (
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <Box
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#FA5000',
                        flexShrink: 0,
                        marginTop: 8,
                    }}
                />
                <Text style={baseTextStyle}>
                    <InlineText text={bulletContent} baseStyle={baseTextStyle} />
                </Text>
            </Box>
        );
    }

    // paragraph
    return (
        <Box style={{ marginBottom: 6 }}>
            <Text style={baseTextStyle}>
                <InlineText text={line} baseStyle={baseTextStyle} />
            </Text>
        </Box>
    );
};

const CenterPolicyModal = (props) => {
    const scrollRef = useRef(null);
    const [atBottom, setAtBottom] = useState(false);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        setAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
    };

    useEffect(() => {
        if (scrollRef.current) {
            const { scrollHeight, clientHeight } = scrollRef.current;
            if (scrollHeight <= clientHeight) setAtBottom(true);
        }
    }, [props?.policy]);

    const policyLines = props?.policy?.split('\n') ?? [];

    return (
        <Modal
            opened={props?.centerPolicy}
            onClose={() => props?.openedCenterPolicyModal(false)}
            closeOnClickOutside={false}
            size="md"
            centered
            title="Centre Policy"
            styles={{
                modal: {
                    borderRadius: 12,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                },
                header: {
                    borderBottom: '1px solid #F2F4F7',
                    paddingBottom: 14,
                    marginBottom: 0,
                },
                title: {
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#1D2939',
                    letterSpacing: '-0.3px',
                },
                close: {
                    color: '#667085',
                    '&:hover': { backgroundColor: '#F2F4F7' },
                },
                body: { padding: 0 },
            }}
        >
            {/* Scrollable content */}
            <Box style={{ position: 'relative' }}>
                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    style={{
                        maxHeight: 400,
                        overflowY: 'auto',
                        padding: '16px 24px',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#D0D5DD transparent',
                    }}
                >
                    {policyLines.map((line) => (
                        <PolicyLine
                            key={`${line.slice(0, 30)}-${line.length}`}
                            line={line}
                        />
                    ))}
                </Box>

                {!atBottom && (
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 48,
                            background: 'linear-gradient(to bottom, transparent, #fff)',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </Box>

            {/* Divider */}
            <Box style={{ height: 1, backgroundColor: '#F2F4F7' }} />

            {/* Footer */}
            <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 24px 18px' }}>
                <Button
                    onClick={() => props?.openedCenterPolicyModal(false)}
                    styles={{
                        root: {
                            backgroundColor: '#FA5000',
                            fontSize: 15,
                            fontWeight: 600,
                            height: 42,
                            padding: '0 28px',
                            borderRadius: 8,
                            transition: 'background 0.2s',
                            '&:hover': { backgroundColor: '#e04800' },
                        },
                    }}
                >
                    Okay
                </Button>
            </Box>
        </Modal>
    );
};

export default CenterPolicyModal;